import { Node, Edge } from 'reactflow'
import { hierarchy, tree } from 'd3-hierarchy'
import { getStepType } from './JourneyEditorUtils'

interface LayoutOptions {
    defaultNodeWidth?: number
    defaultNodeHeight?: number
    horizontalSpacing?: number
    verticalSpacing?: number
}

interface HierarchyNode {
    id: string
    children: HierarchyNode[]
}

/**
 * Auto-layout ReactFlow nodes using d3-hierarchy tree algorithm
 * Handles variable node widths to prevent overlapping
 * Ensures nodes with multiple parents are positioned below all parents
 */
export function autoLayout(
    nodes: Node[],
    edges: Edge[],
    options: LayoutOptions = {},
): Node[] {
    const {
        defaultNodeWidth = 200,
        defaultNodeHeight = 80,
        horizontalSpacing = 50,
        verticalSpacing = 100,
    } = options

    if (nodes.length === 0) return []

    // Additional spacing for edges with EditEdge components (like experiment ratios)
    const edgeLabelSpacing = 200 // Extra vertical spacing
    const edgeLabelWidth = 150 // Approximate width of edge label UI (like ratio inputs)

    // Create node dimension map using computed dimensions from ReactFlow
    const nodeDimensions = new Map<string, { width: number, height: number }>()
    // Track which nodes have EditEdge components that need extra vertical spacing
    const nodesWithEdgeLabels = new Set<string>()

    nodes.forEach(node => {
        // ReactFlow stores computed dimensions after rendering
        const computedWidth = (node as any).computed?.width
            ?? (node as any).width
            ?? (node as any).measured?.width
            ?? defaultNodeWidth
        const computedHeight = (node as any).computed?.height
            ?? (node as any).height
            ?? (node as any).measured?.height
            ?? defaultNodeHeight

        nodeDimensions.set(node.id, {
            width: computedWidth,
            height: computedHeight,
        })

        // Check if this node type has EditEdge component
        const stepType = getStepType(node.data?.type)
        if (stepType?.EditEdge) {
            nodesWithEdgeLabels.add(node.id)
        }
    })

    // Build adjacency map with edge information to preserve ordering
    const childrenWithHandles = new Map<string, Array<{ target: string, sourceHandle?: string | null }>>()
    const children = new Map<string, string[]>()
    const parents = new Map<string, string[]>()

    edges.forEach(edge => {
        const source = edge.source
        const target = edge.target

        if (!childrenWithHandles.has(source)) childrenWithHandles.set(source, [])
        if (!children.has(source)) children.set(source, [])
        if (!parents.has(target)) parents.set(target, [])

        childrenWithHandles.get(source)!.push({ target, sourceHandle: edge.sourceHandle })
        parents.get(target)!.push(source)
    })

    // Sort children for each node to respect source handle ordering (left to right)
    // This ensures gate nodes have yes/no in the correct positions and experiment nodes
    // maintain their left-to-right ordering
    childrenWithHandles.forEach((childList, nodeId) => {
        childList.sort((a, b) => {
            const aHandle = a.sourceHandle ?? ''
            const bHandle = b.sourceHandle ?? ''

            // Extract the source key from handles like "yes-s-nodeId" or "no-s-nodeId"
            // The key is everything before the first "-s-"
            const aKey = aHandle.split('-s-')[0]
            const bKey = bHandle.split('-s-')[0]

            // Define ordering for known source types
            // For gate nodes: yes (0) comes before no (1)
            // For other nodes with named sources, maintain their declaration order
            const sourceOrder: Record<string, number> = {
                yes: 0,
                no: 1,
                '': 999, // Default/unnamed handles come last
            }

            const aOrder = sourceOrder[aKey] ?? (aKey ? 500 : 999)
            const bOrder = sourceOrder[bKey] ?? (bKey ? 500 : 999)

            // If both have defined orders, use them
            if (aOrder !== bOrder) {
                return aOrder - bOrder
            }

            // Otherwise sort alphabetically by the key to maintain consistent ordering
            return aKey.localeCompare(bKey)
        })

        // Now populate the children map with just the target IDs in the correct order
        children.set(nodeId, childList.map(c => c.target))
    })

    // Find root nodes (nodes with no parents)
    const rootIds = nodes
        .map(n => n.id)
        .filter(id => !parents.has(id) || (parents.get(id)?.length ?? 0) === 0)

    // If no roots found (circular graph), pick node with most children
    if (rootIds.length === 0) {
        const nodeWithMostChildren = nodes.reduce((best, node) => {
            const childCount = children.get(node.id)?.length ?? 0
            const bestCount = children.get(best.id)?.length ?? 0
            return childCount > bestCount ? node : best
        })
        rootIds.push(nodeWithMostChildren.id)
    }

    // Build hierarchy trees and detect cycles
    const visited = new Set<string>()
    const hierarchies: any[] = []

    rootIds.forEach(rootId => {
        const hierarchyData = buildHierarchy(rootId, children, visited, new Set())
        const root = hierarchy(hierarchyData)
        hierarchies.push(root)
    })

    // Handle orphaned nodes (not reached from any root)
    const allVisited = new Set(visited)
    nodes.forEach(node => {
        if (!allVisited.has(node.id)) {
            const hierarchyData = buildHierarchy(node.id, children, allVisited, new Set())
            const root = hierarchy(hierarchyData)
            hierarchies.push(root)
        }
    })

    // Apply initial tree layout
    const treeLayout = tree<HierarchyNode>().nodeSize([1, 1])

    let currentXOffset = 0
    const positionMap = new Map<string, { x: number, y: number }>()

    hierarchies.forEach(root => {
        const layoutRoot = treeLayout(root)

        // Calculate subtree widths and depths for each node
        const subtreeWidths = new Map<string, number>()
        const subtreeDepths = new Map<string, number>()
        calculateSubtreeWidths(layoutRoot, nodeDimensions, horizontalSpacing, subtreeWidths, nodesWithEdgeLabels, edgeLabelWidth)
        calculateSubtreeDepths(layoutRoot, nodeDimensions, verticalSpacing, subtreeDepths, nodesWithEdgeLabels, edgeLabelSpacing)

        // Position nodes based on subtree widths and depths
        const positions = new Map<string, { x: number, y: number }>()
        positionNodesWithWidth(
            layoutRoot,
            0,
            0,
            nodeDimensions,
            subtreeWidths,
            subtreeDepths,
            horizontalSpacing,
            verticalSpacing,
            positions,
            nodesWithEdgeLabels,
            edgeLabelSpacing,
            edgeLabelWidth,
        )

        // Find bounds and apply offset
        let minX = Infinity
        let maxX = -Infinity
        positions.forEach(pos => {
            if (pos.x < minX) minX = pos.x
            if (pos.x > maxX) maxX = pos.x
        })

        // Store positions with offset
        positions.forEach((pos, id) => {
            positionMap.set(id, {
                x: pos.x - minX + currentXOffset,
                y: pos.y,
            })
        })

        // Update offset for next tree
        const lastNodeId = Array.from(positions.keys()).reduce((last, id) => {
            return (positions.get(id)?.x ?? 0) > (positions.get(last)?.x ?? 0) ? id : last
        })
        const lastNodeWidth = nodeDimensions.get(lastNodeId)?.width ?? defaultNodeWidth
        currentXOffset = maxX - minX + currentXOffset + lastNodeWidth + horizontalSpacing * 3
    })

    // Adjust positions for nodes with multiple parents
    // Ensure they are positioned below ALL their parents
    adjustMultiParentNodes(positionMap, parents, nodeDimensions, verticalSpacing)

    // Apply positions to nodes
    return nodes.map(node => ({
        ...node,
        position: positionMap.get(node.id) ?? node.position,
    }))
}

function calculateSubtreeWidths(
    node: any,
    nodeDimensions: Map<string, { width: number, height: number }>,
    horizontalSpacing: number,
    subtreeWidths: Map<string, number>,
    nodesWithEdgeLabels: Set<string>,
    edgeLabelWidth: number,
): number {
    const nodeWidth = nodeDimensions.get(node.data.id)?.width ?? 200

    if (!node.children || node.children.length === 0) {
        subtreeWidths.set(node.data.id, nodeWidth)
        return nodeWidth
    }

    // Calculate total width needed for all children
    let childrenWidth = 0
    node.children.forEach((child: any, i: number) => {
        const childWidth = calculateSubtreeWidths(child, nodeDimensions, horizontalSpacing, subtreeWidths, nodesWithEdgeLabels, edgeLabelWidth)
        childrenWidth += childWidth
        if (i < node.children.length - 1) {
            // If this node has edge labels, add extra spacing to prevent label overlap
            // Edge labels appear at midpoint between parent and children, so they're positioned
            // diagonally. We need enough horizontal space so labels don't overlap.
            const spacing = nodesWithEdgeLabels.has(node.data.id)
                ? horizontalSpacing + (edgeLabelWidth * 2)
                : horizontalSpacing
            childrenWidth += spacing
        }
    })

    // Subtree width is the max of node width and children width
    const totalWidth = Math.max(nodeWidth, childrenWidth)
    subtreeWidths.set(node.data.id, totalWidth)
    return totalWidth
}

function calculateSubtreeDepths(
    node: any,
    nodeDimensions: Map<string, { width: number, height: number }>,
    verticalSpacing: number,
    subtreeDepths: Map<string, number>,
    nodesWithEdgeLabels: Set<string>,
    edgeLabelSpacing: number,
): number {
    const nodeHeight = nodeDimensions.get(node.data.id)?.height ?? 80

    if (!node.children || node.children.length === 0) {
        subtreeDepths.set(node.data.id, nodeHeight)
        return nodeHeight
    }

    // Find the maximum depth among all children
    let maxChildDepth = 0
    node.children.forEach((child: any) => {
        const childDepth = calculateSubtreeDepths(child, nodeDimensions, verticalSpacing, subtreeDepths, nodesWithEdgeLabels, edgeLabelSpacing)
        maxChildDepth = Math.max(maxChildDepth, childDepth)
    })

    // Calculate spacing: add extra space if this node has edge labels (EditEdge component)
    const spacing = nodesWithEdgeLabels.has(node.data.id)
        ? verticalSpacing + edgeLabelSpacing
        : verticalSpacing

    // Total depth is this node's height + spacing + max child depth
    const totalDepth = nodeHeight + spacing + maxChildDepth
    subtreeDepths.set(node.data.id, totalDepth)
    return totalDepth
}

/**
 * Position nodes considering their actual widths and heights
 */
function positionNodesWithWidth(
    node: any,
    x: number,
    y: number,
    nodeDimensions: Map<string, { width: number, height: number }>,
    subtreeWidths: Map<string, number>,
    subtreeDepths: Map<string, number>,
    horizontalSpacing: number,
    verticalSpacing: number,
    positions: Map<string, { x: number, y: number }>,
    nodesWithEdgeLabels: Set<string>,
    edgeLabelSpacing: number,
    edgeLabelWidth: number,
): void {
    const nodeWidth = nodeDimensions.get(node.data.id)?.width ?? 200
    const nodeHeight = nodeDimensions.get(node.data.id)?.height ?? 80
    const subtreeWidth = subtreeWidths.get(node.data.id) ?? nodeWidth

    // Center the node within its subtree width
    const nodeX = x + (subtreeWidth - nodeWidth) / 2
    positions.set(node.data.id, { x: nodeX, y })

    if (!node.children || node.children.length === 0) return

    // Calculate vertical spacing: add extra space if this node has edge labels (EditEdge component)
    const vSpacing = nodesWithEdgeLabels.has(node.data.id)
        ? verticalSpacing + edgeLabelSpacing
        : verticalSpacing

    // Calculate starting x for children and y position based on current node height
    let childX = x
    const childY = y + nodeHeight + vSpacing

    node.children.forEach((child: any) => {
        const childSubtreeWidth = subtreeWidths.get(child.data.id) ?? 200

        positionNodesWithWidth(
            child,
            childX,
            childY,
            nodeDimensions,
            subtreeWidths,
            subtreeDepths,
            horizontalSpacing,
            verticalSpacing,
            positions,
            nodesWithEdgeLabels,
            edgeLabelSpacing,
            edgeLabelWidth,
        )

        // Calculate horizontal spacing to next child
        // If this node has edge labels, use increased spacing to prevent label overlap
        // Edge labels appear at midpoint between parent and children, so they're positioned
        // diagonally. We need enough horizontal space so labels don't overlap.
        const hSpacing = nodesWithEdgeLabels.has(node.data.id)
            ? horizontalSpacing + (edgeLabelWidth * 2)
            : horizontalSpacing

        childX += childSubtreeWidth + hSpacing
    })
}

/**
 * Recursively build hierarchy structure, detecting and breaking cycles
 */
function buildHierarchy(
    nodeId: string,
    childrenMap: Map<string, string[]>,
    globalVisited: Set<string>,
    pathVisited: Set<string>,
): HierarchyNode {
    globalVisited.add(nodeId)
    pathVisited.add(nodeId)

    const childIds = childrenMap.get(nodeId) ?? []
    const children: HierarchyNode[] = []

    childIds.forEach(childId => {
        // Skip if creates cycle in current path
        if (pathVisited.has(childId)) return

        // Skip if already visited globally (prevents duplicate subtrees)
        if (globalVisited.has(childId)) return

        children.push(buildHierarchy(childId, childrenMap, globalVisited, new Set(pathVisited)))
    })

    pathVisited.delete(nodeId)

    return {
        id: nodeId,
        children,
    }
}

/**
 * Adjust positions of nodes with multiple parents to ensure they are
 * positioned vertically below ALL their parents, not just one
 */
function adjustMultiParentNodes(
    positionMap: Map<string, { x: number, y: number }>,
    parents: Map<string, string[]>,
    nodeDimensions: Map<string, { width: number, height: number }>,
    verticalSpacing: number,
): void {
    // Process each node that has multiple parents
    parents.forEach((parentIds, nodeId) => {
        if (parentIds.length <= 1) return

        const nodePos = positionMap.get(nodeId)
        if (!nodePos) return

        // Find the maximum bottom position of all parent nodes
        let maxParentBottom = -Infinity

        parentIds.forEach(parentId => {
            const parentPos = positionMap.get(parentId)
            if (!parentPos) return

            const parentDims = nodeDimensions.get(parentId)
            const parentHeight = parentDims?.height ?? 80
            const parentBottom = parentPos.y + parentHeight

            if (parentBottom > maxParentBottom) {
                maxParentBottom = parentBottom
            }
        })

        // If we found valid parent positions, ensure the child is below all of them
        if (maxParentBottom > -Infinity) {
            const minChildY = maxParentBottom + verticalSpacing

            // Only adjust if the child is currently above where it should be
            if (nodePos.y < minChildY) {
                const yOffset = minChildY - nodePos.y

                // Recursively shift this node and all its descendants
                shiftNodeAndDescendants(
                    nodeId,
                    positionMap,
                    parents,
                    yOffset,
                    new Set(),
                )
            }
        }
    })
}

/**
 * Recursively shift a node and all its descendants by a Y offset
 */
function shiftNodeAndDescendants(
    nodeId: string,
    positionMap: Map<string, { x: number, y: number }>,
    parents: Map<string, string[]>,
    yOffset: number,
    visited: Set<string>,
): void {
    if (visited.has(nodeId)) return
    visited.add(nodeId)

    const nodePos = positionMap.get(nodeId)
    if (!nodePos) return

    // Shift this node
    positionMap.set(nodeId, {
        x: nodePos.x,
        y: nodePos.y + yOffset,
    })

    // Find all children of this node (nodes that have this node as a parent)
    parents.forEach((parentIds, childId) => {
        if (parentIds.includes(nodeId)) {
            shiftNodeAndDescendants(childId, positionMap, parents, yOffset, visited)
        }
    })
}
